import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 500;
export const BULK_DELETE_ROLES = ["super_admin", "admin", "tenant_admin", "manager"];

// Select-all + "Delete selected" bar for the Leads list.
export const LeadsBulkDelete = ({ visible, selected, setSelected, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const chosen = visible.filter((l) => selected.has(l.id));
  const allOn = visible.length > 0 && chosen.length === visible.length;
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(visible.slice(0, MAX).map((l) => l.id)));
  const run = async () => {
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/leads/bulk-delete`, { lead_ids: chosen.map((l) => l.id) })).data;
      toast.success(`Deleted ${r.deleted_count} lead${r.deleted_count === 1 ? "" : "s"}${r.skipped ? ` · ${r.skipped} skipped (other location)` : ""}`);
      setSelected(new Set()); setOpen(false); onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); } finally { setBusy(false); }
  };
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2.5 ${chosen.length ? "bg-rose-50 border-rose-200" : "bg-white border-gray-200"}`} data-testid="leads-bulk-bar">
      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer">
        <Checkbox checked={allOn} onCheckedChange={toggleAll} data-testid="leads-select-all" />
        Select all shown ({Math.min(visible.length, MAX)})
      </label>
      {chosen.length > 0 && (
        <>
          <span className="text-sm font-bold text-rose-700" data-testid="leads-selected-count">{chosen.length} selected</span>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="leads-clear-selection"><X className="w-3.5 h-3.5 mr-1" />Clear</Button>
          <Button size="sm" className="ml-auto bg-rose-600 hover:bg-rose-700 text-white" disabled={chosen.length > MAX} onClick={() => setOpen(true)} data-testid="leads-bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" />Delete selected
          </Button>
        </>
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="leads-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {chosen.length} lead{chosen.length === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>This also removes their email history and POS matches. It cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-48 overflow-y-auto rounded-lg border bg-gray-50 p-2 text-sm text-gray-700" data-testid="leads-bulk-delete-names">
            {chosen.map((l) => <div key={l.id}>{l.name || "Unknown Lead"}{l.email ? <span className="text-gray-400"> · {l.email}</span> : null}</div>)}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="leads-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={(e) => { e.preventDefault(); run(); }} data-testid="leads-bulk-delete-confirm">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Delete {chosen.length}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
